// The named colours of the app shell (TGCLOUD-1869).
//
// The rule they serve: colour on the home screen is either BRAND or MEANING,
// never decoration. This module names both sides, so a test can hold every
// colour the home tiles draw to that rule (colourGuard.js):
//
//   BRAND_COLOURS     TrakGene's own colours.
//   CLINICAL_COLOURS  every colour that already says something clinical
//                     somewhere in the product.
//   TILE_COLOURS      the home screen tiles: one hue, one weight. The hue is
//                     the deployment's accent (tileColoursFor); these are the
//                     tiles on the bundled accent.
//
// Dashboard.jsx puts all three on the MUI theme as palette.brand,
// palette.clinical and palette.tile, the last for the deployment's accent.
//
// BRAND and CLINICAL are FIXED, not per deployment. A group's accent
// (`theme.accent`, Settings > Appearance) is a free-text hex that the nav rail,
// the hover tints and the home tiles follow. It is that deployment's brand
// colour, so the tiles draw it as the rail does, as a glyph on a pale tint of
// itself, and never under a white glyph: nothing can promise how white reads
// on it. These values are measured, and the tests hold them to what was
// measured.

export const BRAND_COLOURS = Object.freeze({
  // The TrakGene brand teal: the house palette, and the bundled default of a
  // deployment's accent (CONSTANTS.customTheme.accent). White on it is only
  // 2.79:1, so it is a fill and a tint, never the ground for a white glyph or
  // for small text.
  teal: '#2BA9BF',
  // The brand teal darkened until it clears AA: white on it is 5.64:1. Already
  // the product's teal for anything drawn ON teal or AS teal text: the pedigree
  // sidebar's active label (SidebarSection.jsx), its section index, the person
  // header band and the Add Relative tiles.
  tealAA: '#0E7186',
  navy: '#2E3747'
});

// Every colour that carries clinical meaning, defined here and only here
// (TGCLOUD-1869). The modules named beside each group read their colours from
// this object rather than holding their own, and palette.test.js fails if a
// copy of any of these values reappears as a literal elsewhere in src.
//
// One clinical scale is deliberately kept out: the GenCC gene-disease validity
// chips (CHIP_SX in genotypes-menu/GeneValidityBlock.jsx). They are defined
// once, every chip spells its grade in words, and their hues already sit
// where this register has entries, so listing them would protect nothing.
//
// Note that the brand teal is in here too, as the Phenotype record type: that
// is why the tile guard exempts brand colours rather than measuring them.
export const CLINICAL_COLOURS = Object.freeze({
  // Record types: the notification rail's bar, tint and label, and the record
  // cards (NOTE_TYPE_COLOURS, src/components/Constants.js).
  referral: '#0d8c9e',
  appointment: '#53b382',
  test: '#354051',
  sample: '#d946a8',
  treatment: '#3b82f6',
  address: '#808080',
  clinicalNote: '#f7b661',
  contact: '#f6d956',
  consent: '#87a878',
  tissuePathology: '#8c7ae6',
  process: '#bdbdbd',
  riskScore: '#354051',
  disease: '#d94f4f',
  phenotype: '#2BA9BF',
  genotype: '#7546cc',
  pregnancy: '#c2185b',

  // How late a record is: the notification rail's "Overdue" heading and its
  // past-due badges (DUE_BANDS, src/util/recordDueDates.js). `alert` is also
  // the badge for more than two weeks late.
  alert: '#e05252',
  pastDueTwoWeeks: '#e0602b',
  pastDueOneWeek: '#e0822b',
  pastDue: '#e0a23b',

  // The pedigree.
  lockedPedigree: '#d32f2f', // a person another user is editing (ChartBox.jsx)
  // Variant markers (variantPathogenicity.js). "Classifications differ" is
  // also the Gene Validity warning that GenCC submitters disagree.
  pathogenic: '#D93025',
  uncertainSignificance: '#9E9E9E',
  classificationsDiffer: '#B26A00',
  reviewedDiscrepancy: '#5B7A8C',
  // The confirmed tick (Canvas.jsx) and its legend entries.
  clinicallyConfirmed: '#2e7d32',
  // The analysis's transmission line through its supporting individuals
  // (Canvas.jsx). The brand teal's value, with a meaning of its own.
  analysisPathway: '#2ba9bf',
  // An affected symbol with no disease colour is solid black: the Bennett
  // convention and the SVG default. Drawn by no literal; named so this list
  // and the server's (backend/util/colourSafety.js) match one for one.
  bennettAffected: '#000000',
  cascadePrimary: '#ed6c02', // cascade badges (cascadeBadge.js)
  cascadeSecondary: '#f0a560',
  cascadeDeferred: '#9a8b7e',
  cascadeUnpriced: '#6b7a8c',
  matchStrong: '#2e7d32', // pedigree match confidence (PedigreeMatchMenu.jsx)
  matchModerate: '#ed6c02',
  matchWeak: '#9e9e9e',
  matchRuledOut: '#c62828',
  // The pedigree match's "Review needed" cards: a lab vs ClinVar conflict, and
  // the informational alerts (consanguinity, a data gap, reduced penetrance).
  // The same values as ruled out and moderate, kept apart because the meaning
  // is not the same.
  matchReviewAlert: '#c62828',
  matchInfoAlert: '#ed6c02'
});

// The home screen tiles: one hue, one weight, and no tile has a colour of its
// own. Since TGCLOUD-1896 the hue is the deployment's accent, and every chip is
// drawn as the nav rail draws the page you are on (CustomDrawer's
// StyledNavLink): the glyph in the accent, on a tint of it. TGCLOUD-1869 drew
// them all in a fixed slate (#5B6673 on #EDF3F5); after a week Tim found it
// dull beside the rail, "a muted version of grey" (9 October 2026). (Two
// weights, a solid brand chip for four "primary" tiles, was tried before that;
// Tim chose one weight on 1 October 2026.)
//
// The tints are the accent mixed with white. At rest 12%, a little more than
// the rail's 8%, because the rail's tint lies on its grey and the chip's on a
// white card. Under a hovered card 20%: ModulesGrid tints a hovered card with
// the accent at 5%, which would all but swallow the resting chip, so the chip
// steps darker and keeps its step from its card (1.12 at rest on the bundled
// accent, 1.16 hovered).
//
// On the bundled accent the glyph is 2.5:1 on its chip, as the rail's own
// selected icon is on its tint. That is below the 3:1 a graphic that must be
// read needs; it is accepted because the chip is decoration (aria-hidden)
// beside the tile's title. Both tints are near-neutral (chroma 5.3 and 8.8),
// so only the glyph needs the brand exemption from the clinical register.
const TILE_TINT = 0.12;
const TILE_TINT_HOVERED = 0.2;

// An accent as #rrggbb, or null when it is not a hex colour.
const sixDigitHex = (colour) => {
  const hex = String(colour ?? '').trim();
  if (/^#[0-9a-f]{6}$/i.test(hex)) return hex;
  if (/^#[0-9a-f]{3}$/i.test(hex)) {
    return hex.replace(/[0-9a-f]/gi, (c) => c + c);
  }
  return null;
};

// `hex` mixed with white: `amount` of the colour, the rest white.
const tintOf = (hex, amount) =>
  '#' +
  [1, 3, 5]
    .map((i) => parseInt(hex.slice(i, i + 2), 16))
    .map((c) => Math.round(255 - (255 - c) * amount))
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('');

/**
 * palette.tile for a deployment's accent (`theme.accent`). An accent that is
 * not a hex colour draws the bundled brand teal.
 *
 * @param {string} accent
 * @returns {{ chipBg: string, glyph: string, chipBgHover: string }}
 */
export const tileColoursFor = (accent) => {
  const glyph = sixDigitHex(accent) ?? BRAND_COLOURS.teal;
  return Object.freeze({
    chipBg: tintOf(glyph, TILE_TINT),
    glyph,
    chipBgHover: tintOf(glyph, TILE_TINT_HOVERED)
  });
};

// The tiles on the bundled accent, the brand teal: what a render without the
// app's theme draws.
export const TILE_COLOURS = tileColoursFor(BRAND_COLOURS.teal);

/**
 * palette.tile from an MUI theme, or the tokens themselves where the app's
 * theme is not mounted (a test, an isolated render). The two are the same
 * values, so the fallback can never draw a different colour.
 */
export const tilePalette = (theme) => theme?.palette?.tile ?? TILE_COLOURS;
